import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { List as MenuIcon, X, CaretDown as ChevronDown } from '@phosphor-icons/react';
import { useCountry } from '../../context/CountryContext';

const LandingNav = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [mobileCountryDropdownOpen, setMobileCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef(null);
  const location = useLocation();
  const { country, setCountry, currentCountry, supportedCountries, detecting } = useCountry();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) {
      setMobileCountryDropdownOpen(false);
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/features', label: 'Features' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/about', label: 'About' },
    { to: '/blog', label: 'Blog' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 safe-area-top ${
      isScrolled ? 'bg-primaryB-50/80 backdrop-blur-xl border-b border-primaryB-200/60 shadow-card dark:bg-primaryB-900/80 dark:border-primaryB-700/40' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img src="/images/logo.png" alt="SmartPOS Africa" className="w-10 h-10 rounded-2xl shadow-card" />
            <span className="text-primaryB-900 font-extrabold text-xl">Smart POS</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`font-semibold transition-all duration-200 ${
                  isActive(link.to)
                    ? 'text-emerald-600'
                    : 'text-primaryB-500 hover:text-primaryB-900'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {/* Country Dropdown */}
            <div className="relative" ref={countryDropdownRef}>
              <button
                onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                className="flex items-center gap-1.5 text-primaryB-500 hover:text-primaryB-900 font-semibold transition-all duration-200 px-3 py-2 rounded-xl hover:bg-primaryB-900/[0.03]"
              >
                {detecting
                  ? <span className="text-lg">🌍</span>
                  : <img src={currentCountry.flagUrl} alt={currentCountry.name} className="w-5 h-3.5 object-cover rounded-sm" />}
                <ChevronDown weight="bold" className={`w-4 h-4 transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {countryDropdownOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-primaryB-50 border border-primaryB-200/60 rounded-2xl shadow-elevated dark:bg-primaryB-900 dark:border-primaryB-700/40 overflow-hidden z-50">
                  {Object.values(supportedCountries).map((c) => (
                    <button
                      key={c.code}
                      onClick={() => {
                        setCountry(c.code);
                        setCountryDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-primaryB-100 transition-all duration-200 ${
                        country === c.code ? 'bg-emerald-500/[0.06] text-emerald-800' : 'text-primaryB-500'
                      }`}
                    >
                      <img src={c.flagUrl} alt={c.name} className="w-5 h-3.5 object-cover rounded-sm flex-shrink-0" />
                      <span className="font-semibold">{c.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/login"
              className="text-primaryB-500 hover:text-primaryB-900 font-semibold transition-all duration-200"
            >
              Login
            </Link>
            <Link
              to="/signup"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-bold transition-all duration-200 shadow-card"
            >
              Start Free Trial
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-primaryB-900 p-2"
          >
            {mobileMenuOpen ? <X weight="bold" className="w-6 h-6" /> : <MenuIcon weight="bold" className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Backdrop */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 top-16 bg-primaryB-900/10 backdrop-blur-sm z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div
          className="md:hidden bg-primaryB-50 border-t border-primaryB-200/60 relative z-50 shadow-elevated dark:bg-primaryB-900 dark:border-primaryB-700/40"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 py-4 space-y-1">
            <div className="flex items-center justify-between">
              <Link
                to="/"
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
                className={`py-2 font-medium ${
                  isActive('/') ? 'text-emerald-600' : 'text-primaryB-500'
                }`}
              >
                Home
              </Link>

              {/* Mobile Country Dropdown */}
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setMobileCountryDropdownOpen(!mobileCountryDropdownOpen);
                  }}
                  className="flex items-center gap-1.5 text-primaryB-500 hover:text-primaryB-900 font-semibold transition-all duration-200 px-3 py-2 rounded-xl hover:bg-primaryB-900/[0.03]"
                >
                  {detecting
                    ? <span className="text-lg">🌍</span>
                    : <img src={currentCountry.flagUrl} alt={currentCountry.name} className="w-5 h-3.5 object-cover rounded-sm" />}
                  <ChevronDown weight="bold" className={`w-4 h-4 transition-transform ${mobileCountryDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {mobileCountryDropdownOpen && (
                  <div className="absolute right-0 mt-1 w-36 bg-primaryB-50 border border-primaryB-200/60 rounded-2xl shadow-elevated dark:bg-primaryB-900 dark:border-primaryB-700/40 overflow-hidden z-50">
                    {Object.values(supportedCountries).map((c) => (
                      <button
                        key={c.code}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCountry(c.code);
                          setMobileCountryDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-primaryB-100 transition-all duration-200 ${
                          country === c.code ? 'bg-emerald-500/[0.06] text-emerald-800' : 'text-primaryB-500'
                        }`}
                      >
                        <img src={c.flagUrl} alt={c.name} className="w-5 h-3.5 object-cover rounded-sm flex-shrink-0" />
                        <span className="font-semibold">{c.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {navLinks.slice(1).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
                className={`block py-2 font-medium ${
                  isActive(link.to)
                    ? 'text-emerald-600'
                    : 'text-primaryB-500'
                }`}
              >
                {link.label}
              </Link>
            ))}

            <div className="pt-4 border-t border-primaryB-200/60 space-y-3">
              <Link
                to="/login"
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
                className="block text-primaryB-500 font-semibold py-2"
              >
                Login
              </Link>
              <Link
                to="/signup"
                onClick={() => {
                  setMobileMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
                className="block bg-emerald-600 text-white text-center px-5 py-3 rounded-full font-bold"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default LandingNav;
